import Vue from 'vue';
import Analytics from 'analytics';
import { TrackingEventsEnum } from '@/enums';
import { enablePlugins } from './enablePlugins';
import {
    jitsu,
    klaviyo,
    facebookPixel,
    googleTagManager,
    intercom,
    frontChat,
    superchat,
    tawkto
} from './index';

/**
 * Initialize analytics with plugins based on configuration
 * @param {Object} config - Configuration for analytics
 * @param {string} config.googleTagManagerId - Google Tag Manager ID
 * @param {string} config.klaviyoApiKey - Klaviyo API Key
 * @param {string} config.facebookPixelId - Facebook Pixel ID
 * @param {string} config.frontChatId - Front Chat ID
 * @param {string} config.superchatApplicationKey - Superchat Application Key
 * @param {string} config.accountId - Account ID
 * @param {string} config.venueId - Venue ID
 * @param {string} config.slug - Venue slug
 * @param {boolean} config.isStoreDesigner - Whether in store designer mode
 * @returns {Object|null} The analytics instance or null if in store designer mode
 */
export function initializeAnalytics(config) {
    if (config.isStoreDesigner) {
        return null;
    }

    const plugins = [];

    if (config.googleTagManagerId) {
        const venueGoogleTagManager = Object.assign(
            {},
            googleTagManager({
                containerId: config.googleTagManagerId,
                enabled: false
            }),
            {
                name: 'google-tag-venue'
            }
        );

        plugins.push(venueGoogleTagManager);

        venueGoogleTagManager.updateConsent({
            config: venueGoogleTagManager.config,
            settings: {
                ad_storage: 'granted',
                analytics_storage: 'granted',
                functionality_storage: 'granted',
                personalization_storage: 'granted',
                security_storage: 'granted',
                ad_user_data: 'granted',
                ad_personalization: 'granted'
            }
        });
    }

    if (config.klaviyoApiKey) {
        plugins.push(klaviyo({ apiKey: config.klaviyoApiKey, enabled: false }));
    }

    if (import.meta.env.VITE_JITSU_CLIENT_KEY) {
        plugins.push(
            jitsu({
                accountId: config.accountId,
                venueId: config.venueId,
                slug: config.slug,
                enabled: false
            })
        );
    }

    if (config.facebookPixelId) {
        plugins.push(
            facebookPixel({
                apiKey: config.facebookPixelId,
                enabled: false
            })
        );
    }

    if (config.frontChatId) {
        plugins.push(
            frontChat({
                chatId: config.frontChatId,
                enabled: false
            })
        );
    }

    if (config.superchatApplicationKey) {
        plugins.push(
            superchat({
                applicationKey: config.superchatApplicationKey,
                enabled: false
            })
        );
    }

    if (config.tawktoPropertyId && config.tawktoWidgetId) {
        plugins.push(
            tawkto({
                propertyId: config.tawktoPropertyId,
                widgetId: config.tawktoWidgetId,
                enabled: false
            })
        );
    }

    const sushidogIds = [8709, 7187, 7571, 7182, 7572, 9017, 9315];
    if (sushidogIds.includes(config.venueId)) {
        plugins.push(
            intercom({
                appId: 'glazwf8n'
            })
        );
    }

    const analyticsInstance = Analytics({
        app: 'storekit',
        debug: import.meta.env.DEV,
        plugins
    });

    Vue.prototype.$analytics = analyticsInstance;

    return analyticsInstance;
}

/**
 * Track an analytics event
 * @param {string} event - Event name from TrackingEventsEnum
 * @param {Object} properties - Event properties
 */
export function trackEvent(event, properties) {
    if (Vue.prototype.$analytics) {
        Vue.prototype.$analytics.track(event, properties);
    }
}

/**
 * Initialize analytics for a venue
 * @param {Object} venue - Venue object
 * @param {boolean} isStoreDesigner - Whether in store designer mode
 */
export function initializeVenueAnalytics(venue, isStoreDesigner) {
    const config = {
        googleTagManagerId: venue.googleTagManagerId,
        klaviyoApiKey: venue.klaviyoApiKey,
        facebookPixelId: venue.facebookPixelId,
        frontChatId: venue.frontChatId,
        superchatApplicationKey: venue.superchatApplicationKey,
        tawktoPropertyId: venue.tawktoPropertyId,
        tawktoWidgetId: venue.tawktoWidgetId,
        accountId: venue.accountId,
        venueId: venue.id,
        slug: venue.slug,
        isStoreDesigner
    };

    const analyticsInstance = initializeAnalytics(config);

    if (analyticsInstance) {
        trackEvent(TrackingEventsEnum.ACTIVE_ON_SITE, {
            accountId: venue.accountId,
            id: venue.id,
            slug: venue.slug
        });
    }

    const pluginData = {
        googleTagManagerId: venue.googleTagManagerId,
        klaviyoApiKey: venue.klaviyoApiKey,
        facebookPixelId: venue.facebookPixelId,
        frontChatId: venue.frontChatId,
        superchatApplicationKey: venue.superchatApplicationKey,
        tawktoPropertyId: venue.tawktoPropertyId,
        tawktoWidgetId: venue.tawktoWidgetId
    };

    enableAnalyticsPlugins(pluginData);

    if (Vue.prototype.$growthbook?.flushExperimentQueue) {
        Vue.prototype.$growthbook.flushExperimentQueue();
    }

    return analyticsInstance;
}

/**
 * Initialize analytics for a store group
 * @param {Object} storeGroup - Store group object
 */
export function initializeStoreGroupAnalytics(storeGroup) {
    const config = {
        googleTagManagerId: storeGroup.googleTagManagerId,
        klaviyoApiKey: storeGroup.klaviyoApiKey,
        facebookPixelId: storeGroup.facebookPixelId,
        accountId: storeGroup.accountId,
        venueId: null,
        slug: storeGroup.slug,
        isStoreDesigner: false
    };

    const analyticsInstance = initializeAnalytics(config);

    if (analyticsInstance) {
        trackEvent(TrackingEventsEnum.ACTIVE_ON_SITE, {
            accountId: storeGroup.accountId,
            brandId: storeGroup.id,
            slug: storeGroup.slug
        });
    }

    return analyticsInstance;
}

/**
 * Enable analytics plugins
 * @param {Object} pluginData - Plugin data
 */
export function enableAnalyticsPlugins(pluginData) {
    enablePlugins(pluginData, Vue.prototype.$analytics);
}
